import { FormattedMessage } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useStore } from 'zustand'

import { EyeIcon } from '@gto/icons-react/outline/eye'
import { Button } from '@gto/ui/button'
import { Typography } from '@gto/ui/typography'

import { store } from '@/store'

/**
 * Persistent top banner shown across the portal shell whenever admin
 * impersonation is active. The "am I impersonating" signal comes from
 * the impersonation state — NOT from `store.partner`, which becomes
 * the target partner profile while impersonation is active.
 */
export const ImpersonationBanner = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const impersonation = useStore(store, (s) => s.impersonation)

  if (impersonation === null) {
    return null
  }

  const exit = async () => {
    store.getState().setImpersonation(null)
    void queryClient.invalidateQueries()
    // refetchPartner is contractually non-throwing: a tagged
    // RefetchPartnerResult comes back instead. An 'unauthenticated' outcome
    // means the admin's own session expired while they were impersonating —
    // landing them on /admin/partners would just trigger _private.tsx's
    // beforeLoad to bounce them to /login, so go straight there. Success and
    // 'error' both return the admin to the partners list.
    //
    // The asymmetry with the enter path (which shows an inline error by the
    // "Log in as" button) is INTENTIONAL: exit clears impersonation FIRST, so
    // this banner unmounts — there is no host left for an inline error — and it
    // lands on /admin/partners, a working page that refetches its own data. A
    // transient refetch failure here is self-healing (impersonation is genuinely
    // cleared, the admin is on a usable page) and still reaches Sentry, so a
    // dedicated error surface would have nowhere to live and nothing actionable
    // to say.
    const result = await store.getState().refetchPartner()
    if (result.status === 'unauthenticated') {
      void navigate({ to: '/login' })
    } else {
      void navigate({ to: '/admin/partners' })
    }
  }

  return (
    <div
      className="mb-1 flex items-center justify-between gap-2 rounded-md bg-warning-main/15 px-4 py-2 text-warning-main"
      data-tst="impersonation-banner"
    >
      <div className="flex items-center gap-2">
        <EyeIcon
          height={18}
          width={18}
        />
        <Typography
          color="inherit"
          variant="body2"
        >
          <FormattedMessage
            id="impersonation.banner.viewingAs"
            values={{ name: impersonation.display_name }}
          />
        </Typography>
      </div>
      <Button
        color="warning"
        data-tst="impersonation-exit"
        size="small"
        variant="contained"
        onClick={() => {
          void exit()
        }}
      >
        <FormattedMessage id="impersonation.banner.exit" />
      </Button>
    </div>
  )
}
